import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Switch, Modal, message } from "antd";
import { useTranslation } from "react-i18next";
import { FiShield, FiKey, FiSmartphone, FiActivity, FiArrowRight, FiAlertTriangle, FiTrash2, FiPause } from "react-icons/fi";
import { requestDisableAccount, requestDeleteAccount } from "../../api/account/accountAction";

const SecurityInfo = () => {
  const { t } = useTranslation();
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const securityOptions = [
    {
      icon: FiActivity,
      color: "#10b981",
      bgColor: "#f0fdf4",
      title: t("profile.plans.saveMyActivity"),
      description: t("profile.plans.saveMyActivityInfo"),
      type: "switch",
      disabled: true,
    },
    {
      icon: FiKey,
      color: "#8B5CF6",
      bgColor: "#f5f3ff",
      title: t("profile.plans.changePassword"),
      description: t("profile.plans.changePasswordInfo"),
      type: "link",
      link: "/profile/password",
    },
    {
      icon: FiSmartphone,
      color: "#7C3AED",
      bgColor: "#ede9fe",
      title: t("profile.plans.twoFactorAuth"),
      description: t("profile.plans.twoFactorAuthInfo"),
      type: "button",
      disabled: true,
      buttonText: t("profile.plans.disable"),
    },
  ];

  const handleDisableAccount = async () => {
    setIsProcessing(true);
    try {
      const { data } = await requestDisableAccount();
      if (data.success) {
        setShowDisableModal(false);
        message.success(data.message || "Verification email sent. Please check your inbox.");
      } else {
        message.error(data.error || "Failed to send verification email.");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "An error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsProcessing(true);
    try {
      const { data } = await requestDeleteAccount();
      if (data.success) {
        setShowDeleteModal(false);
        message.success(data.message || "Verification email sent. Please check your inbox.");
      } else {
        message.error(data.error || "Failed to send verification email.");
      }
    } catch (error) {
      message.error(error.response?.data?.error || "An error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "20px 24px" }}>
      {/* Page Header */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "4px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiShield size={20} color="white" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 600, color: "#1e293b" }}>
              {t("profile.plans.securitySettings")}
            </h1>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
              {t("profile.plans.securitySettingsInfo")}
            </p>
          </div>
        </div>
      </div>

      {/* Security Options */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {securityOptions.map((option, idx) => {
          const Icon = option.icon;
          return (
            <div
              key={idx}
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                padding: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: 1 }}>
                <div style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "10px",
                  background: option.bgColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Icon size={20} color={option.color} />
                </div>
                <div>
                  <h3 style={{
                    margin: 0,
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1e293b",
                    marginBottom: "4px",
                  }}>
                    {option.title}
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#64748b",
                    lineHeight: 1.4,
                  }}>
                    {option.description}
                  </p>
                </div>
              </div>

              {option.type === "switch" && (
                <Switch disabled={option.disabled} />
              )}

              {option.type === "link" && (
                <Link to={option.link}>
                  <button
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 16px",
                      borderRadius: "8px",
                      border: "none",
                      background: option.color,
                      color: "#fff",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option.title}
                    <FiArrowRight size={14} />
                  </button>
                </Link>
              )}

              {option.type === "button" && (
                <button
                  disabled={option.disabled}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    color: "#94a3b8",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    opacity: option.disabled ? 0.6 : 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.buttonText}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Danger Zone */}
      <div style={{ marginTop: "32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiAlertTriangle size={20} color="white" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#ef4444" }}>
              Danger Zone
            </h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
              Irreversible account actions
            </p>
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            overflow: "hidden",
          }}
        >
          {/* Disable Account */}
          <div
            style={{
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: 1 }}>
              <div style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <FiPause size={20} color="#f59e0b" />
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: "4px",
                }}>
                  Disable Account
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#64748b",
                  lineHeight: 1.4,
                }}>
                  Temporarily disable your account. Your data will be preserved.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDisableModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 16px",
                borderRadius: "8px",
                border: "1px solid #f59e0b",
                background: "transparent",
                color: "#f59e0b",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
            >
              Disable
            </button>
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: "#f1f5f9" }} />

          {/* Delete Account */}
          <div
            style={{
              padding: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: 1 }}>
              <div style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <FiTrash2 size={20} color="#ef4444" />
              </div>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1e293b",
                  marginBottom: "4px",
                }}>
                  Delete Account
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#64748b",
                  lineHeight: 1.4,
                }}>
                  Permanently delete your account and all data. This cannot be undone.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 16px",
                borderRadius: "8px",
                border: "none",
                background: "#ef4444",
                color: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Disable Account Modal */}
      <Modal
        open={showDisableModal}
        onCancel={() => !isProcessing && setShowDisableModal(false)}
        footer={null}
        centered
        width={440}
        closable={!isProcessing}
      >
        <div style={{ padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiAlertTriangle size={24} color="#f59e0b" />
            </div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#1e293b" }}>
              Disable Account
            </h3>
          </div>

          <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#475569" }}>
            Are you sure you want to disable your account?
          </p>

          <ul style={{ margin: "0 0 16px", paddingLeft: "20px", color: "#64748b", fontSize: "13px" }}>
            <li style={{ marginBottom: "8px" }}>Your account will be temporarily deactivated</li>
            <li style={{ marginBottom: "8px" }}>You won't be able to use Copilotly features</li>
            <li style={{ marginBottom: "8px" }}>Your data will be safely preserved</li>
            <li style={{ marginBottom: "8px" }}>You can reactivate by contacting support</li>
            <li style={{ marginBottom: "8px", color: "#f59e0b", fontWeight: 500 }}>
              Any active subscription will be cancelled
            </li>
          </ul>

          <div
            style={{
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
              A verification email will be sent to confirm this action.
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowDisableModal(false)}
              disabled={isProcessing}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDisableAccount}
              disabled={isProcessing}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid #f59e0b",
                background: "transparent",
                color: "#f59e0b",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              {isProcessing ? "Sending..." : "Send Verification Email"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Account Modal */}
      <Modal
        open={showDeleteModal}
        onCancel={() => !isProcessing && setShowDeleteModal(false)}
        footer={null}
        centered
        width={440}
        closable={!isProcessing}
      >
        <div style={{ padding: "8px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FiTrash2 size={24} color="#ef4444" />
            </div>
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#ef4444" }}>
              Delete Account
            </h3>
          </div>

          <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#ef4444", fontWeight: 500 }}>
            This action is permanent and cannot be undone!
          </p>

          <ul style={{ margin: "0 0 16px", paddingLeft: "20px", color: "#64748b", fontSize: "13px" }}>
            <li style={{ marginBottom: "8px" }}>All your data will be permanently deleted</li>
            <li style={{ marginBottom: "8px" }}>Your saved copilots and chats will be removed</li>
            <li style={{ marginBottom: "8px" }}>This action cannot be reversed</li>
            <li style={{ marginBottom: "8px", color: "#ef4444", fontWeight: 500 }}>
              Any active subscription will be cancelled immediately
            </li>
          </ul>

          <div
            style={{
              padding: "12px",
              background: "#fef2f2",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #fecaca",
            }}
          >
            <p style={{ margin: 0, fontSize: "12px", color: "#dc2626" }}>
              A verification email will be sent to confirm this permanent action.
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setShowDeleteModal(false)}
              disabled={isProcessing}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                color: "#475569",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={isProcessing}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                background: "#ef4444",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 500,
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              {isProcessing ? "Sending..." : "Send Verification Email"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SecurityInfo;
